import React, { useState } from 'react';
import LoaderButton from '../../components/LoaderButton';
import ForgotPasswordDialog from '../../components/ForgotPasswordDialog';
import { Typography, ButtonBase, TextField, Paper, FormGroup, FormControlLabel, Checkbox } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import AppBarContext from '../../contexts/AppBarContext';
import { toastError, toastSuccess } from '../../utils/Functions';
import { trackPromise } from 'react-promise-tracker';
import { authenticate, sendPasswordReset } from '../../utils/AccountsAPI';
import { useLoadContext } from '../../hooks/useLoadContext';
import { globalAny } from './NavbarWithSidebar';

const maxLoginWidth = 300;

const useStyles = makeStyles((theme) => ({
  title: {
    fontFamily: '"Aldrich", "Courier", monospace',
    color: theme.palette.primary.main,
  },
  input: {
    width: '100%',
    minWidth: '150px',
    maxWidth: maxLoginWidth + 'px',
    margin: 'auto',
  },
  paper: {
    padding: theme.spacing(3),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
      maxWidth: maxLoginWidth + 20 + 'px',
    },
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      top: '56px',
      left: 0,
      width: '100%',
      height: 'calc(100% - 56px)',
      zIndex: 1101,
      boxSizing: 'border-box',
    },
    overflow: 'hidden',
  },
  loginForm: {
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      width: '100%',
      height: '1px',
      boxSizing: 'border-box',
      top: 'calc(100% - ' + theme.spacing(3) + 'px)',
      left: 0,
      margin: 'auto',
    },
  },
  form: {
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    paddingTop: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      transform: 'translateY(-100%)',
      backgroundColor: 'rgba(0,0,0,.35)',
      borderRadius: theme.spacing(1),
    },
  },
  logo: {
    padding: theme.spacing(3),
  },
  button: {
    marginTop: theme.spacing(3),
    minWidth: '150px',
    color: 'white',
    margin: 'auto',
    width: '100%',
    borderRadius: 0,
    padding: theme.spacing(1),
    maxWidth: maxLoginWidth + 'px',
  },
  forgotPassword: {
    marginRight: '0',
    marginLeft: 'auto',
    display: 'block',
    marginTop: theme.spacing(1),
  },
  rememberMe: {
    float: 'left',
    margin: '0',
  },
  checkboxRoot: {
    fontSize: '1rem',
  },
  loginFeatures: {
    width: '100%',
    maxWidth: maxLoginWidth + 'px',
    margin: 'auto',
  },
  spacer: {
    width: '100%',
    height: theme.spacing(3),
  },
}));

export default function Login(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const theme = useTheme();
  const classes = useStyles(theme);
  const mobile = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true });

  useLoadContext(AppBarContext, { title: 'TIPS Login' });

  const validateForm = () => {
    return username.length > 0 && password.length > 0;
  };

  const handleSubmit = (event?) => {
    event?.preventDefault();

    if (!validateForm()) {
      toastError(props.toast, 'Please enter username and password.');
      return;
    }

    setIsLoading(true);

    trackPromise(authenticate(username.trim(), password, rememberMe))
      .then((user) => {
        var location = props.location.state ? props.location.state.from.pathname : null;
        var query = props.location.state ? props.location.state.from.search : null;
        setIsLoading(false);
        props.userHasAuthenticated(true, (location ?? '') + (query ?? ''), user, rememberMe);
      })
      .catch((err) => {
        setIsLoading(false);
        toastError(props.toast, err);
      });
  };

  const handlePasswordReset = (username) => {
    trackPromise(
      sendPasswordReset(username)
        .then((response) => {
          toastSuccess(props.toast, 'Email sent to account associated with: ' + username);
        })
        .catch((err) => toastError(props.toast, err))
    );
  };

  const keyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Paper className={classes.paper} elevation={3} square={mobile}>
      <Typography className={classes.title} variant='h1'>
        TIPS
      </Typography>
      {mobile ? <img alt='SDSMA Logo' src={'favicon.png'} width='50%' className={classes.logo} /> : null}
      {
        <Typography className={classes.title} variant='h4'>
          {globalAny.appVersion}
        </Typography>
      }
      <div className={classes.loginForm}>
        <FormGroup onSubmit={handleSubmit} className={classes.form}>
          <TextField autoFocus variant='standard' type='text' value={username} onChange={(e) => setUsername(e.target.value)} className={classes.input} label='Username' />
          <div className={classes.spacer} />
          <TextField
            variant='standard'
            type='password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={keyPress}
            className={classes.input}
            label='Password'
          />
          <div className={classes.loginFeatures}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => {
                    setRememberMe(e.target.checked);
                  }}
                  value='rememberMe'
                  color='primary'
                  size='small'
                  classes={{ root: classes.checkboxRoot }}
                />
              }
              label='Remember Me'
              labelPlacement='start'
              className={classes.rememberMe}
              classes={{ label: 'MuiTypography-caption' }}
            />
            <ButtonBase onClick={() => setDialogOpen(true)} className={classes.forgotPassword}>
              <Typography variant='caption'>Forgot Password ?</Typography>
            </ButtonBase>
          </div>
          <LoaderButton type='submit' isLoading={isLoading} onClick={handleSubmit} text='Login' loadingText='Logging in...' className={classes.button} />
        </FormGroup>
      </div>
      {dialogOpen ? <ForgotPasswordDialog open={dialogOpen} handleClose={() => setDialogOpen(false)} childProps={props} handlePasswordReset={handlePasswordReset} /> : null}
    </Paper>
  );
}
